// TODO: next PR for GEN-2388 will add properly components based on helix design system with the refactored code.
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import {
  CONSENT_IMAGE,
  ConsentFlowCard,
  ConsentLanguageErrorPanel,
  ContextLoadingScreen,
  ErrorPanel,
  LoadingPanel,
  WorkflowProgress,
} from '../features/consent/consentOptions/ConsentFlowLayout'
import { ConsentIntroScreen, ConsentSignScreen } from '../features/consent/consentOptions/PrimaryConsentStep'
import { SecondaryChoiceScreen, SecondaryIntroScreen } from '../features/consent/consentOptions/SecondaryConsentStep'
import { getPrimaryCheckboxText, normalizePath } from '../features/consent/consentOptions/consentOptionsUtils'
import { usePrimaryConsent } from '../features/consent/consentOptions/usePrimaryConsent'
import { useSecondaryConsent } from '../features/consent/consentOptions/useSecondaryConsent'
import { useConsentConfig } from '../features/consent/hooks/useConsentConfig'
import { useConsentContext } from '../features/consent/hooks/useConsentContext'
import { useConsentLanguage } from '../features/consent/hooks/useConsentLanguage'
import { useSecondaryConsentConfig } from '../features/consent/hooks/useSecondaryConsentConfig'
import { useStageTracking } from '../hooks/useStageTracking'
import { track } from '../utils/analytics'
import { isPractitionerMediatedConsentFlow } from '../utils/consentFlowHelper'
import { withSupportMessage } from '../utils/errorMessage'

const ConsentOptions = () => {
  const intl = useIntl()
  const location = useLocation()
  const navigate = useNavigate()
  const path = normalizePath(location.pathname)
  const consentContext = useConsentContext()
  const {
    language: consentLanguage,
    loading: consentLanguageLoading,
    error: consentLanguageError,
  } = useConsentLanguage()
  const secondaryConsentSource = useSecondaryConsentConfig()
  const consentConfig = useConsentConfig(
    secondaryConsentSource.payload,
    secondaryConsentSource.loading,
    secondaryConsentSource.error
  )
  const secondaryConsentConfigId = consentConfig.consentConfig?.configId ?? null
  const secondaryConsentConfigVersionId = consentConfig.consentConfig?.configVersionId ?? null
  const secondaryConsentConfig = useMemo(() => {
    if (!secondaryConsentConfigId || !secondaryConsentConfigVersionId) return null
    return {
      configId: secondaryConsentConfigId,
      configVersionId: secondaryConsentConfigVersionId,
    }
  }, [secondaryConsentConfigId, secondaryConsentConfigVersionId])
  const prevPathnameRef = useRef(location.pathname)
  const primaryFlowCompletedRef = useRef(false)
  const markPrimaryFlowCompleted = useCallback(() => {
    primaryFlowCompletedRef.current = true
  }, [])
  const primaryConsent = usePrimaryConsent(
    consentContext.context,
    consentConfig.flowStartedAt,
    markPrimaryFlowCompleted,
    consentLanguage
  )
  const secondaryConsent = useSecondaryConsent(secondaryConsentConfig, intl.locale)

  useStageTracking(path)

  useEffect(() => {
    primaryFlowCompletedRef.current = primaryConsent.isFlowCompleted
  }, [primaryConsent.isFlowCompleted])

  useEffect(() => {
    if (consentLanguageError === 'consent_not_applicable') {
      primaryFlowCompletedRef.current = true
    }
  }, [consentLanguageError])

  useEffect(() => {
    return () => {
      if (!primaryFlowCompletedRef.current) {
        track('consent.drop_off', {
          stage: normalizePath(prevPathnameRef.current),
          reason: 'spa_navigation',
        })
      }
    }
  }, [])

  useEffect(() => {
    const onPopState = () => {
      track('consent.back_navigation', {
        from: normalizePath(location.pathname),
        to: normalizePath(prevPathnameRef.current),
      })
      prevPathnameRef.current = location.pathname
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [location.pathname])

  const selectedSecondaryPage = path.startsWith('/consents/secondary/')
    ? Number(path.split('/').filter(Boolean)[2])
    : undefined
  const selectedSecondaryIndex =
    selectedSecondaryPage !== undefined && Number.isInteger(selectedSecondaryPage) && selectedSecondaryPage >= 2
      ? selectedSecondaryPage - 2
      : undefined
  const selectedSecondaryItem =
    selectedSecondaryIndex !== undefined && Number.isInteger(selectedSecondaryIndex)
      ? consentConfig.secondaryItems[selectedSecondaryIndex]
      : undefined

  if (consentContext.contextLoading) {
    return <ContextLoadingScreen />
  }

  if (consentContext.contextError || consentContext.contextMissing || !consentContext.hasContext) {
    return (
      <ConsentFlowCard
        imageSrc={CONSENT_IMAGE}
        layout="vertical"
        progress={
          <WorkflowProgress
            current={[consentContext.workflowSteps.consent]}
            next={[consentContext.workflowSteps.secondary, consentContext.workflowSteps.profile]}
          />
        }
      >
        <ErrorPanel
          message={
            consentContext.contextError
              ? withSupportMessage(
                  intl,
                  intl.formatMessage({
                    id: 'consentOptions.error.couldNotLoadTestPatient',
                    defaultMessage: "We couldn't load the test and patient details.",
                  })
                )
              : intl.formatMessage({
                  id: 'consentOptions.error.couldNotLoadContext',
                  defaultMessage: "We couldn't load the test and patient details required to provide consent.",
                })
          }
        />
      </ConsentFlowCard>
    )
  }

  const practitionerMediated = isPractitionerMediatedConsentFlow(consentContext.context?.consentFlow)
  if (practitionerMediated && (path === '/consents' || path === '/consents/sign')) {
    return <Navigate replace to="/consents/secondary" />
  }

  const primaryStepProgress = (
    <WorkflowProgress
      current={[consentContext.workflowSteps.consent]}
      next={[consentContext.workflowSteps.secondary, consentContext.workflowSteps.profile]}
    />
  )

  if (path === '/consents' || path === '/consents/sign') {
    if (consentLanguageLoading) {
      return (
        <ConsentFlowCard imageSrc={CONSENT_IMAGE} progress={primaryStepProgress}>
          <LoadingPanel
            message={intl.formatMessage({
              id: 'consentOptions.loadingPanel.message',
              defaultMessage: 'Loading consent details...',
            })}
          />
        </ConsentFlowCard>
      )
    }
    if (consentLanguageError === 'consent_not_applicable') {
      return (
        <ConsentIntroScreen
          alreadySigned={primaryConsent.confirmed}
          noPendingPrimaryOrders
          workflowSteps={consentContext.workflowSteps}
        />
      )
    }
    if (!consentLanguage) {
      // Also guards documentContent completeness (FAM-108 / G3): usePrimaryConsent.confirm() only
      // sends documentContent when consentLanguage is truthy, and it's passed the same
      // consentLanguage value used here. Rendering ConsentSignScreen (and its confirm handler)
      // before this check would let a user submit consent with no documentContent recorded.
      return (
        <ConsentFlowCard imageSrc={CONSENT_IMAGE} progress={primaryStepProgress}>
          <ConsentLanguageErrorPanel error={consentLanguageError} />
        </ConsentFlowCard>
      )
    }
    if (path === '/consents/sign') {
      return (
        <ConsentSignScreen
          checkboxText={getPrimaryCheckboxText(consentContext.context, consentLanguage)}
          agreed={primaryConsent.agreed}
          onAgreedChange={primaryConsent.setAgreed}
          confirmed={primaryConsent.confirmed}
          signedAt={primaryConsent.signedAt}
          saving={primaryConsent.saving}
          errorMessage={primaryConsent.error}
          onConfirm={primaryConsent.confirm}
          primaryFlowNotice={primaryConsent.showPrimaryFlowNotice}
          workflowSteps={consentContext.workflowSteps}
          language={consentLanguage}
        />
      )
    }
    return <ConsentIntroScreen alreadySigned={primaryConsent.confirmed} workflowSteps={consentContext.workflowSteps} />
  }

  // Secondary consent texts load independently of primary, so gate only the secondary steps on them.
  if (path.startsWith('/consents/secondary')) {
    if (consentConfig.consentConfigLoading) {
      return (
        <ConsentFlowCard
          imageSrc={CONSENT_IMAGE}
          layout="vertical"
          progress={
            <WorkflowProgress
              current={[consentContext.workflowSteps.consent]}
              next={[consentContext.workflowSteps.secondary, consentContext.workflowSteps.profile]}
            />
          }
        >
          <LoadingPanel
            message={intl.formatMessage({
              id: 'consentOptions.loadingPanel.message',
              defaultMessage: 'Loading consent details...',
            })}
          />
        </ConsentFlowCard>
      )
    }
    if (consentConfig.consentConfigError) {
      return (
        <ConsentFlowCard
          imageSrc={CONSENT_IMAGE}
          layout="vertical"
          progress={
            <WorkflowProgress
              current={[consentContext.workflowSteps.consent]}
              next={[consentContext.workflowSteps.secondary, consentContext.workflowSteps.profile]}
            />
          }
        >
          <ErrorPanel message={consentConfig.consentConfigError} />
        </ConsentFlowCard>
      )
    }
  }

  if (path === '/consents/secondary') {
    return (
      <SecondaryIntroScreen
        hasItems={consentConfig.secondaryItems.length > 0}
        workflowSteps={consentContext.workflowSteps}
      />
    )
  }

  if (path === '/consents/secondary/1') {
    return <Navigate replace to={consentConfig.secondaryItems.length > 0 ? '/consents/secondary/2' : '/home'} />
  }

  if (path.startsWith('/consents/secondary/') && selectedSecondaryItem) {
    const item = selectedSecondaryItem.item
    const sectionTitle = selectedSecondaryItem.sectionTitle
    const keySelf = `${item.id}:SELF`
    const keyDep = `${item.id}:DEPENDENT`
    const valueSingle = secondaryConsent.toggles[item.id]
    const valueSelf = secondaryConsent.toggles[keySelf]
    const valueDependent = secondaryConsent.toggles[keyDep]
    const dependentSubtitle = consentContext.context?.patientFirstName?.trim()
      ? intl.formatMessage(
          { id: 'consentOptions.secondaryChoice.forPatient', defaultMessage: 'For {name}' },
          { name: consentContext.context.patientFirstName.trim() }
        )
      : consentContext.context?.patientName?.trim()
        ? intl.formatMessage(
            { id: 'consentOptions.secondaryChoice.forPatient', defaultMessage: 'For {name}' },
            { name: consentContext.context.patientName.trim() }
          )
        : intl.formatMessage({
            id: 'consentOptions.secondaryChoice.forYourChild',
            defaultMessage: 'For your child',
          })

    return (
      <SecondaryChoiceScreen
        item={item}
        dualSecondary={consentContext.dualSecondary}
        dependentSubtitle={dependentSubtitle}
        workflowSteps={consentContext.workflowSteps}
        step={selectedSecondaryPage}
        value={consentContext.dualSecondary ? undefined : valueSingle}
        valueSelf={consentContext.dualSecondary ? valueSelf : undefined}
        valueDependent={consentContext.dualSecondary ? valueDependent : undefined}
        onSelect={
          consentContext.dualSecondary
            ? undefined
            : (nextValue: boolean) => secondaryConsent.setToggles((prev) => ({ ...prev, [item.id]: nextValue }))
        }
        onSelectSelf={
          consentContext.dualSecondary
            ? (nextValue: boolean) => secondaryConsent.setToggles((prev) => ({ ...prev, [keySelf]: nextValue }))
            : undefined
        }
        onSelectDependent={
          consentContext.dualSecondary
            ? (nextValue: boolean) => secondaryConsent.setToggles((prev) => ({ ...prev, [keyDep]: nextValue }))
            : undefined
        }
        saving={secondaryConsent.saving}
        onContinue={async () => {
          if (consentContext.dualSecondary) {
            if (valueSelf === undefined || valueDependent === undefined) {
              track('consent.validation_error', {
                item_id: item.id,
                field: 'secondary_dual_choice',
                missing_self: valueSelf === undefined,
                missing_dependent: valueDependent === undefined,
              })
              return
            }
            const saved = await secondaryConsent.saveDualChoices(item, sectionTitle, valueSelf, valueDependent)
            if (!saved) return
          } else {
            if (valueSingle === undefined) {
              track('consent.validation_error', { item_id: item.id, field: 'secondary_choice' })
              return
            }
            const saved = await secondaryConsent.saveChoice(item, sectionTitle, valueSingle)
            if (!saved) return
          }
          const nextIndex = selectedSecondaryIndex! + 1
          const to = nextIndex < consentConfig.secondaryItems.length ? `/consents/secondary/${nextIndex + 2}` : '/home'
          track('consent.stage_continue', { from: path, to, item_id: item.id })
          navigate(to)
        }}
      />
    )
  }

  if (path.startsWith('/consents/secondary/')) {
    return (
      <SecondaryIntroScreen
        hasItems={consentConfig.secondaryItems.length > 0}
        workflowSteps={consentContext.workflowSteps}
      />
    )
  }

  return <ConsentIntroScreen alreadySigned={primaryConsent.confirmed} workflowSteps={consentContext.workflowSteps} />
}

export default ConsentOptions
